import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import ApplyWeddingTheme from "@/components/ApplyWeddingTheme";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Play, Pause, Mic, AlertTriangle } from "lucide-react";

export default function GuestGallery() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [memories, setMemories] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      const wr = await api.get(`/public/weddings/${slug}`); setW(wr.data);
      const mr = await api.get(`/public/weddings/${slug}/memories`); setMemories(mr.data);
    })();
  }, [slug]);

  if (!w) return <div className="min-h-screen grid place-items-center text-neutral-500">Loading...</div>;
  const accent = w.theme?.accent_color || "#C7A76C";
  const activeIdx = active != null ? memories.findIndex(m => m.id === active) : -1;
  const activeMem = activeIdx >= 0 ? memories[activeIdx] : null;
  const move = (dir) => {
    if (activeIdx < 0) return;
    const n = (activeIdx + dir + memories.length) % memories.length;
    setActive(memories[n].id);
  };

  return (
    <div className="min-h-screen relative pt-6 pb-24 px-4" data-testid="gallery-page">
      <ApplyWeddingTheme theme={w.theme} />
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <Link to={`/w/${slug}`} className="text-xs text-neutral-500 hover:underline inline-flex items-center gap-1" data-testid="gallery-back"><ArrowLeft className="w-3 h-3" /> Back to wedding</Link>
            <h1 className="font-serif text-4xl sm:text-5xl mt-1">{w.display_name}</h1>
            <div className="text-neutral-600 text-sm">{memories.length} memories from your guests</div>
          </div>
          <Link to={`/w/${slug}/upload`} className="rounded-full px-5 py-3 text-sm text-white" style={{ background: accent }} data-testid="gallery-share-btn">
            {w.upload_button_text}
          </Link>
        </div>
        {memories.length === 0 ? (
          <div className="glass p-14 text-center">
            <div className="font-serif text-2xl">No memories yet</div>
            <p className="text-neutral-600 mt-2">Be the first to share.</p>
          </div>
        ) : (
          <div className="glass p-2 sm:p-3 overflow-hidden" data-testid="memory-grid">
            <div className="grid grid-cols-3 gap-[2px] sm:gap-1 bg-neutral-900/10 rounded-2xl overflow-hidden">
              {memories.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setActive(m.id)}
                  className="relative aspect-square bg-neutral-200 overflow-hidden group focus:outline-none"
                  data-testid={`gallery-item-${m.id}`}
                >
                  {m.media_type === "video" ? (
                    m.thumbnail_url ? (
                      <img src={`${BACKEND_URL}${m.thumbnail_url}`} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-neutral-900" />
                    )
                  ) : (
                    <img src={`${BACKEND_URL}${m.media_url}`} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                  )}
                  {m.media_type === "video" && (
                    <div className="absolute inset-0 grid place-items-center bg-black/25">
                      <span className="w-12 h-12 rounded-full bg-white/95 grid place-items-center shadow-lg">
                        <Play className="w-5 h-5 text-neutral-900" fill="currentColor" />
                      </span>
                    </div>
                  )}
                  {m.voice_url && (
                    <div className="absolute top-2 right-2 glass-subtle rounded-full w-7 h-7 grid place-items-center" title="Voice message">
                      <Mic className="w-3.5 h-3.5" style={{ color: accent }} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {activeMem && (
        <MemoryDetail
          memory={activeMem}
          onClose={() => setActive(null)}
          onPrev={() => move(-1)}
          onNext={() => move(1)}
          index={activeIdx}
          total={memories.length}
          accent={accent}
        />
      )}
    </div>
  );
}

function VoicePlayer({ src, accent }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => { setFailed(false); setPlaying(false); setCur(0); setDur(0); }, [src, attempt]);

  const onLoaded = (e) => {
    const d = e.currentTarget.duration;
    if (isFinite(d) && d > 0) setDur(d);
  };
  const onTime = (e) => setCur(e.currentTarget.currentTime);
  const onEnded = () => { setPlaying(false); setCur(0); };
  const onError = (e) => {
    // eslint-disable-next-line no-console
    console.error("Voice playback error", e?.currentTarget?.error);
    setFailed(true); setPlaying(false);
  };
  const toggle = async () => {
    const a = audioRef.current; if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { try { await a.play(); setPlaying(true); } catch (err) { setFailed(true); } }
  };
  const seek = (e) => {
    const a = audioRef.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * dur;
    setCur(pct * dur);
  };
  const fmt = (s) => {
    if (!isFinite(s)) s = 0;
    const m = Math.floor(s/60); const ss = Math.floor(s%60);
    return `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
  };
  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;

  if (failed) {
    return (
      <div className="glass-subtle p-4 bg-white/70" data-testid="voice-player-error">
        <div className="flex items-start gap-2 text-sm text-neutral-800">
          <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: accent }} />
          <div className="flex-1">
            <div className="font-medium">Unable to play this voice message.</div>
            <div className="text-xs text-neutral-600">The audio failed to load.</div>
          </div>
          <button onClick={() => setAttempt(a => a + 1)} className="rounded-full px-3 py-1 text-xs text-white" style={{ background: accent }} data-testid="voice-player-retry">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-subtle p-4 bg-white/70" data-testid="voice-player">
      <audio ref={audioRef} src={src} preload="metadata" onLoadedMetadata={onLoaded} onDurationChange={onLoaded} onTimeUpdate={onTime} onEnded={onEnded} onError={onError} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} className="hidden" />
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-full grid place-items-center text-white" style={{ background: accent }}><Mic className="w-4 h-4" /></span>
        <div className="text-xs uppercase tracking-widest text-neutral-500">Voice message</div>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={toggle} className="w-10 h-10 rounded-full grid place-items-center text-white" style={{ background: accent }} data-testid="voice-player-toggle" aria-label={playing ? "Pause" : "Play"}>
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" fill="currentColor" />}
        </button>
        <div className="flex-1">
          <div onClick={seek} className="h-2 rounded-full bg-neutral-200 cursor-pointer relative" data-testid="voice-player-seek">
            <div className="h-2 rounded-full absolute left-0 top-0" style={{ width: `${pct}%`, background: accent }} />
          </div>
          <div className="flex justify-between text-xs text-neutral-500 mt-1 tabular-nums">
            <span>{fmt(cur)}</span><span>{fmt(dur)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MemoryDetail({ memory, onClose, onPrev, onNext, index, total, accent }) {
  const voiceSrc = useMemo(() => memory.voice_url ? `${BACKEND_URL}${memory.voice_url}` : null, [memory.voice_url]);
  const mediaSrc = `${BACKEND_URL}${memory.media_url}`;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="memory-modal">
      <div className="glass-strong max-w-5xl w-full max-h-[95vh] overflow-y-auto p-3 sm:p-4 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 btn-ghost-glass rounded-full p-2 z-10" data-testid="modal-close" aria-label="Close"><X className="w-4 h-4" /></button>
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-4">
          <div className="bg-black rounded-2xl overflow-hidden grid place-items-center">
            {memory.media_type === "video" ? (
              <video src={mediaSrc} controls autoPlay={false} playsInline className="w-full max-h-[70vh]" data-testid="modal-video" />
            ) : (
              <img src={mediaSrc} alt="" className="w-full max-h-[70vh] object-contain" data-testid="modal-image" />
            )}
          </div>
          <div className="p-2 flex flex-col">
            <div className="text-xs uppercase tracking-widest text-neutral-500">{new Date(memory.created_at).toLocaleDateString()}</div>
            <div className="font-serif text-3xl mt-1" data-testid="modal-guest-name">{memory.guest_name || "Anonymous"}</div>
            {memory.wish_text && (
              <div className="mt-4 glass-subtle p-4 bg-white/70" data-testid="modal-wish">
                <div className="font-serif text-lg italic leading-snug">"{memory.wish_text}"</div>
              </div>
            )}
            {voiceSrc && (
              <div className="mt-4" data-testid="modal-voice">
                <VoicePlayer src={voiceSrc} accent={accent} />
              </div>
            )}
            <div className="flex items-center gap-2 mt-6">
              <button onClick={onPrev} className="btn-ghost-glass rounded-full p-2" data-testid="modal-prev" aria-label="Previous"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={onNext} className="btn-ghost-glass rounded-full p-2" data-testid="modal-next" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
              <div className="text-xs text-neutral-500 ml-auto">{index + 1} / {total}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
